import { Button, Paragraph } from '@zillow/constellation-10';
import { useContext, useMemo } from 'react';
import { ModalType } from '../../../../types/ModalType';
import {
  emitInviteToApplySidebarButtonClickEvent,
  INVITE_TO_APPLY_SIDEBAR_BUTTON_CLICK_EVENT_TYPE_ID,
} from '../../../../constants/clickstreamEvents/actionSidebar';
import StringConstants from '../../../../constants/StringConstants';
import useInviteToApply from '../../../../hooks/useInviteToApply';
import { ACTION_TEXT } from '../../../../utils/getApplicationActionText';
import useModal from '../../../../hooks/useModal';
import { AppContext } from '../../../../contexts/AppContextProvider';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import InboxSidebarSection from '../InboxSidebarSection';
import ApplicationsUpsellModal from '../../../Modals/ApplicationsUpsellModal';
import TermsOfUse from '../../../TermsOfUse';
import { TooltipWrapper } from '@zg-rentals/satellite-components';
import { useAuth } from '@zg-rentals/auth-comet';

const InviteToApply = (): JSX.Element | null => {
  const { isImpersonator } = useContext(AppContext);
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const { activeModal } = useModal();
  const { user } = useAuth();

  const {
    actionText,
    handleInviteToApplyClick,
    helperText,
    isApplicationsAllowed,
    isConversationArchived,
    isConversationBlockedByZillow,
    isConversationMarkedAsSpam,
    isLoading,
    isSendingInvite,
    viewApplicationsForListingUrl,
  } = useInviteToApply({
    conversationId: conversationId || '',
    listingAlias: listingAlias || '',
    shouldShowApplicationUpsellModal: true,
  });

  const inviteToApplyTooltipText = useMemo(() => {
    if (isImpersonator) {
      return StringConstants.TOOLTIP.IMPERSONATION;
    }
    if (isConversationBlockedByZillow) {
      return StringConstants.TOOLTIP.BLOCKED_BY_ZILLOW;
    }
    if (isConversationArchived || isConversationMarkedAsSpam) {
      return StringConstants.TOOLTIP.INVITE_TO_APPLY_MOVE_TO_INBOX;
    }
    return '';
  }, [isConversationArchived, isConversationBlockedByZillow, isConversationMarkedAsSpam, isImpersonator]);

  if (isLoading || !isApplicationsAllowed || !actionText) {
    return null;
  }

  const handleInviteToApplyClickWrapper = () => {
    emitInviteToApplySidebarButtonClickEvent(user);

    return handleInviteToApplyClick();
  };

  return (
    <InboxSidebarSection title="Next step">
      <>
        {activeModal === ModalType.ApplicationsUpsellModal && (
          <ApplicationsUpsellModal listingAlias={listingAlias || ''} />
        )}

        <TooltipWrapper wrapInTooltip={!!inviteToApplyTooltipText} tooltipText={inviteToApplyTooltipText}>
          {actionText === ACTION_TEXT.VIEW_APPLICATION ? (
            <Button
              asChild
              data-testid="view-application-btn"
              disabled={!!inviteToApplyTooltipText || isLoading}
              emphasis="filled"
              fluid={true}
              tone="brand"
            >
              <a href={viewApplicationsForListingUrl} target="_blank" rel="noreferrer">
                {ACTION_TEXT.VIEW_APPLICATION}
              </a>
            </Button>
          ) : (
            <Button
              data-eventid={INVITE_TO_APPLY_SIDEBAR_BUTTON_CLICK_EVENT_TYPE_ID}
              tone={actionText === ACTION_TEXT.RESEND_APPLICATION ? 'neutral' : 'brand'}
              emphasis={actionText === ACTION_TEXT.RESEND_APPLICATION ? 'outlined' : 'filled'}
              data-testid="send-application-btn"
              disabled={!!inviteToApplyTooltipText || isLoading || isSendingInvite}
              fluid={true}
              onClick={handleInviteToApplyClickWrapper}
            >
              {isSendingInvite ? 'Sending...' : actionText}
            </Button>
          )}
        </TooltipWrapper>

        {helperText.text && (
          <Paragraph
            textStyle="body-sm"
            css={{
              color: helperText.isFailure ? 'red.600' : 'green.500',
              marginTop: 'tighter',
              marginBottom: 'tighter',
            }}
          >
            {helperText.text}
          </Paragraph>
        )}

        <TermsOfUse actionText={actionText} />
      </>
    </InboxSidebarSection>
  );
};

export default InviteToApply;
